import { Box, Stack, Typography, styled } from "@mui/material";
import { NO_OP_CALLBACK } from "@smartrr/shared/constants";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { useTypedForm } from "@smartrr/shared/hooks/useTypedForm";
import { isLeapYear } from "@smartrr/shared/utils/dateUtils";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { CircleChevronDown } from "akar-icons";
import React, { useCallback } from "react";
import { toast } from "react-toastify";
import * as yup from "yup";

import { updateCustomersBirthday } from "@customer-app/state/actionCreators/customerRelationship/index";
import { useCloseModal } from "@customer-app/state/actionCreators/modal";
import { useSmartrrCustomerDispatch } from "@customer-app/state/typedCustomerReduxHooks";

import { AquariusFormRow, SelectWrapper } from "./AddressForm";
import { MONTHS_LONG } from "../OrderHistory/utils";

export interface IBirthdayModal {
  custRelId: string;
}

const generateYearSelectMenuItems = () => {
  const menuItems: { label: string; value: number }[] = [];
  const currentYear = new Date().getUTCFullYear();
  for (let i = 0; i < currentYear - 1909; i++) {
    const val = currentYear - i;
    menuItems.push({ label: val.toString(), value: val });
  }
  return menuItems;
};

const generateDayOptions = (month: number, year: number) => {
  let maxDays = 31;
  if (+month === 2) {
    maxDays = isLeapYear(+year) ? 29 : 28;
  } else if ([2, 4, 6, 9, 11].includes(+month)) {
    maxDays = 30;
  }
  return [...new Array(maxDays)].map((d, i) => ({
    label: i + 1 + "",
    value: i + 1,
  }));
};

const monthOptions = MONTHS_LONG.map((m, i) => ({
  label: m,
  value: i + 1,
}));

const Icon = styled(CircleChevronDown)`
  position: absolute;
  right: 10px;
  top: 50%;
  svg {
    stroke: ${props => props.theme.modernIconsColor};
  }
`;

const Wrapper = styled(Box)`
  .form-validation-error {
    position: absolute;
    right: 33px;
    top: 46%;
    width: max-content;
  }
`;

const ButtonWrapper = styled(Box)`
  border-top: 1px solid ${props => props.theme.modernLineDividers};
  button:hover {
    margin-top: 15px;
  }
`;

export const BirthdayModal = ({ custRelId }: IBirthdayModal) => {
  const dispatch = useSmartrrCustomerDispatch();
  const closeModal = useCloseModal();
  const onUpdateBirthday = useCallback(
    (day: number, month: number, year: number) => {
      dispatch(updateCustomersBirthday(custRelId, new Date(year, month - 1, day).toISOString())).then(res => {
        if (res.type === "ERROR_UPDATING_CUSTOMERS_BIRTHDAY") {
          toast.error("Invalid date");
        } else {
          toast("Birthday added!");
          closeModal();
        }
      });
    },
    [custRelId]
  );

  function createBirthdayValidationSchema() {
    return yup.object().shape({
      month: yup.number().required(),
      day: yup.number().required(),
      year: yup.number().required(),
    });
  }

  const today = new Date();

  const { useField, useValues } = useTypedForm({
    validationSchema: createBirthdayValidationSchema(),
    initialValues: { day: today.getUTCDate(), month: today.getUTCMonth() + 1, year: today.getFullYear() },
    onSubmit: NO_OP_CALLBACK,
  });

  const { day, month, year } = useValues();

  const { Dropdown: MonthInput, ValidationError: MonthInputValidation } = useField("month");
  const { Dropdown: DayInput, ValidationError: DayInputValidation } = useField("day");
  const { Dropdown: YearInput, ValidationError: YearInputValidation } = useField("year");

  return (
    <Wrapper>
      <Stack justifyContent="center" alignItems="center" textAlign="center" py={"30px"}>
        <Typography variant="h3" sx={{ marginBottom: "0px !important" }}>
          {typedLocalize("settings.personalInformation.birthdayTitle")}
        </Typography>
        <AquariusFormRow container spacing={"10px"} sx={{ marginTop: "0px !important" }}>
          <SelectWrapper item xs={12} md={4} height="max-content">
            <MonthInput options={monthOptions} />
            <Icon strokeWidth={2} size={18} />
            <MonthInputValidation hideIfFormClean useIcons />
          </SelectWrapper>
          <SelectWrapper item xs={12} md={4} height="max-content">
            <DayInput options={generateDayOptions(month, year)} />
            <Icon strokeWidth={2} size={18} />
            <DayInputValidation hideIfFormClean useIcons />
          </SelectWrapper>
          <SelectWrapper item xs={12} md={4} height="max-content">
            <YearInput options={generateYearSelectMenuItems()} />
            <Icon strokeWidth={2} size={18} />
            <YearInputValidation hideIfFormClean useIcons />
          </SelectWrapper>
        </AquariusFormRow>
      </Stack>
      <ButtonWrapper>
        <AquariusButton filled onClick={() => onUpdateBirthday(day, month, year)} sx={{ margin: "0px" }}>
          {typedLocalize("general.confirmBtn")}
        </AquariusButton>
      </ButtonWrapper>
    </Wrapper>
  );
};
